import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getYearGroupLabel } from "../../Constant/ConstantData";
import { formatWeekCommencingDate } from "../../Util/HelperFunction";

const MultiLineRegistrationChart = ({ registerData, userData }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const colors = {
    year1: "#4318ff",
    year2: "#8553f4",
    year3: "#c900a4",
    year4: "#6c35db",
    year5: "#249a95",
    year6: "#f8ad4d",
    year7: "#fcd46b",
    year8: "#fb8acb",
    year9: "#be00ab",
    year10: "#160071",
    year11: "#c900a4",
    year12: "#6c35db",
    year13: "#249a95",
    year14: "#6c35db",
    year15: "#249a95",
    year16: "#f8ad4d",
    year18: "#f8ad4d",
    parent: "#249a95",
  };

  const yearKeys = useMemo(() => {
    return registerData
      ? [
          ...new Set(
            registerData.flatMap((item) =>
              Object.keys(item).filter(
                (key) => key.startsWith("year") || key === "parent"
              )
            )
          ),
        ].sort((a, b) => {
          if (a === "parent") return 1;
          if (b === "parent") return -1;
          const numA = parseInt(a.replace("year", ""));
          const numB = parseInt(b.replace("year", ""));
          return numA - numB;
        })
      : [];
  }, [registerData]);


  const processedData = useMemo(() => {
    if (!registerData || registerData.length === 0) return [];


    const dataWithAllYears = registerData.map((item) => {
      const processedItem = { ...item };
      yearKeys.forEach((yearKey) => {
        if (!(yearKey in processedItem)) {
          processedItem[yearKey] = 0;
        }
      });
      return processedItem;
    });


    const cumulativeData = [];
    const runningTotals = {};


    yearKeys.forEach((yearKey) => {
      runningTotals[yearKey] = 0;
    });

    dataWithAllYears.forEach((item) => {
      const cumulativeItem = { ...item };

      yearKeys.forEach((yearKey) => {
        runningTotals[yearKey] += item[yearKey] || 0;
        cumulativeItem[yearKey] = runningTotals[yearKey];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [registerData, yearKeys]);

  const maxVal = useMemo(() => {
    if (!processedData || processedData.length === 0) return 15;
    let max = 0;
    processedData.forEach((item) => {
      yearKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [processedData, yearKeys]);

  const yAxisTicks = useMemo(() => {
    let limit = maxVal || 5;
    let step = 1;

    if (limit <= 5) {
      limit = Math.max(4, limit + 1);
      step = 1;
    } else if (limit <= 10) {
      limit = Math.ceil(limit / 2) * 2;
      step = 2;
    } else if (limit <= 20) {
      limit = Math.ceil(limit / 5) * 5;
      step = 5;
    } else if (limit <= 50) {
      limit = Math.ceil(limit / 10) * 10;
      step = 10;
    } else if (limit <= 100) {
      limit = Math.ceil(limit / 20) * 20;
      step = 20;
    } else if (limit <= 200) {
      limit = Math.ceil(limit / 50) * 50;
      step = 50;
    } else if (limit <= 500) {
      limit = Math.ceil(limit / 100) * 100;
      step = 100;
    } else if (limit <= 1000) {
      limit = Math.ceil(limit / 200) * 200;
      step = 200;
    } else {
      limit = Math.ceil(limit / 500) * 500;
      step = 500;
    }

    const ticks = [];
    for (let i = 0; i <= limit; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxVal]);

  const requirements = useMemo(() => {
    const N = processedData?.length || 0;
    if (N <= 1) {
      return { shouldScroll: false, chartWidth: "100%", interval: 0 };
    }
    const requiredWidth = (N - 1) * 90 + 120;
    const availableWidth = (width > 55 ? width - 55 : width) || 500;
    const shouldScroll = availableWidth < requiredWidth;
    
    return {
      shouldScroll,
      chartWidth: shouldScroll ? `${requiredWidth}px` : "100%",
      interval: 0
    };
  }, [processedData?.length, width]);

  const renderTooltipContent = ({ payload, label }) => {
    if (payload && payload.length > 0) {
      const filteredPayload = payload.filter((item) => item.value !== 0);
      if (filteredPayload.length === 0) return null;

      const total = filteredPayload.reduce((sum, item) => sum + item.value, 0);

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px 14px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #e0e5f2",
            fontFamily: "Raleway, sans-serif",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "6px", color: "#2B3674" }}>
            {formatWeekCommencingDate(label)}
          </div>
          {filteredPayload.map((item, index) => (
            <div
              key={index}
              style={{
                color: item.color,
                fontSize: "13px",
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                padding: "2px 0",
              }}
            >
              <span>{item.name}:</span>
              <strong>{item.value}</strong>
            </div>
          ))}
          <div
            style={{
              borderTop: "1px solid #e0e5f2",
              marginTop: "6px",
              paddingTop: "6px",
              fontWeight: "bold",
              color: "#2B3674",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Total:</span>
            <span>{total}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLines = () => {
    return yearKeys.map((yearKey) => {
      const displayName =
        yearKey === "parent"
          ? "Parents"
          : getYearGroupLabel(yearKey.replace("year", "")) ||
            `Year ${yearKey.replace("year", "")}`;
      return (
        <Line
          key={yearKey}
          type="monotone"
          dataKey={yearKey}
          stroke={colors[yearKey] || "#666"}
          name={displayName}
          dot={{ r: 4, strokeWidth: 2, fill: "#ffffff" }}
          activeDot={{ r: 6, strokeWidth: 2, fill: colors[yearKey] || "#666" }}
          strokeWidth={2.5}
          connectNulls={false}
        />
      );
    });
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      {!requirements.shouldScroll ? (
        /* Unified Chart when horizontal scroll is not needed */
        <div style={{ width: "100%", height: 340 }}>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart
              data={processedData}
              margin={{
                top: 15,
                right: 30,
                left: 10,
                bottom: 25,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatWeekCommencingDate}
                tick={{ fontSize: 11, fill: "#707EAE" }}
                angle={-45}
                textAnchor="end"
                height={85}
                dx={-4}
                dy={6}
                interval={requirements.interval}
                padding={{ left: 50, right: 35 }}
              />
              <YAxis
                allowDecimals={false}
                ticks={yAxisTicks}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                interval={0}
                tick={{ fontSize: 12, fill: "#707EAE" }}
                width={45}
              />
              <Tooltip
                labelFormatter={formatWeekCommencingDate}
                content={renderTooltipContent}
              />
              {renderLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        /* Scrollable layout with frozen Y-axis */
        <div
          style={{
            display: "flex",
            width: "100%",
            position: "relative",
          }}
        >
          {/* Frozen Left Y-Axis */}
          <div
            style={{
              width: "55px",
              flexShrink: 0,
              height: 340,
              zIndex: 2,
            }}
          >
            <ResponsiveContainer width="100%" height={340}>
              <LineChart
                data={processedData}
                margin={{
                  top: 15,
                  right: 0,
                  left: 10,
                  bottom: 25,
                }}
              >
                <YAxis
                  allowDecimals={false}
                  ticks={yAxisTicks}
                  domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                  interval={0}
                  tick={{ fontSize: 12, fill: "#707EAE" }}
                  width={45}
                />
                <XAxis
                  dataKey="date"
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                  height={85}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Scrollable Plot Area */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              boxSizing: "border-box",
            }}
            className="chart-scroll-container"
          >
            <div
              style={{
                width: requirements.chartWidth,
                minWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <ResponsiveContainer height={340}>
                <LineChart
                  data={processedData}
                  margin={{
                    top: 15,
                    right: 30,
                    left: 10,
                    bottom: 25,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatWeekCommencingDate}
                    tick={{ fontSize: 11, fill: "#707EAE" }}
                    angle={-45}
                    textAnchor="end"
                    height={85}
                    dx={-4}
                    dy={6}
                    interval={requirements.interval}
                    padding={{ left: 50, right: 35 }}
                  />
                  <YAxis
                    hide
                    allowDecimals={false}
                    ticks={yAxisTicks}
                    domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                    interval={0}
                  />
                  <Tooltip
                    labelFormatter={formatWeekCommencingDate}
                    content={renderTooltipContent}
                  />
                  {renderLines()}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Legend below the chart */}
      {yearKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "15px",
            marginTop: "15px",
          }}
        >
          {yearKeys.map((yearKey) => {
            const isParent = yearKey === "parent";
            const rawYear = yearKey.replace("year", "");
            const name = isParent
              ? "Parents"
              : getYearGroupLabel(rawYear) || `Year ${rawYear}`;
            const color = colors[yearKey] || (isParent ? colors.parent : "#666");

            return (
              <div
                key={yearKey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: color,
                    borderRadius: "2px",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  {name}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  .col-xxl-7,
  .col-xxl-5 {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .cmn-card-color.table-cell {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  .details-chart {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  .chart-scroll-container {
    -webkit-overflow-scrolling: touch;
  }
  .chart-scroll-container::-webkit-scrollbar {
    height: 8px;
  }
  .chart-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb {
    background: #c3c3c3;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`}</style>
    </div>
  );
};

export default MultiLineRegistrationChart;
